/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { Dropdown } from 'react-bootstrap';

import TBody from '../../components/table/TBody/TBody';
import TD from '../../components/table/TD/TD';
import TR from '../../components/table/TR/TR';
import Table from '../../components/table/Table';
import TH from '../../components/table/TH/TH';
import THead from '../../components/table/THead/THead';
import Button from '../../components/form/button/Button';
import Icon from '../../components/icon/Icon';
import TableWrapper from '../components/table-wrapper/TableWrapper';
import { RootState } from '../../core/state/state';
import NoResultsPage from '../../components/no-results-page/NoResultsPage';
import { changeSubcontractorVisibility, getExternalUsersByProjectId } from '../../core/state/subcontractors/asyncActions';
import AddExternalUserModal from '../external-users/modals/add-external-user/AddExternalUserModal';
import { ExternalUserStatus } from '../../core/enums/ExternalUserStatus';
import Badge from '../components/badge/Badge';
import { Color } from '../../core/enums/Color';
import { DATE_FORMAT_EU_DOT } from '../../core/constants/date';
import ManageSubcontractorRoleModal from '../external-users/modals/manage-subcontractor-role/ManageSubcontractorRoleModal';
import { isReadAllRole } from '../../core/state/user/selectors';
import ManageExternalUserModal from '../external-users/modals/manage-external-user/ManageExternalUserModal';
import ActionDropdown from '../../components/action-dropdown/ActionDropdown';
import ConfirmModal from '../components/confirm-modal/ConfirmModal';
import { sendLoginData } from '../../core/state/project/asyncActions';
import { useInternalUser } from '../../core/hooks/useInternalUser';
import { spawnGlobalNotification } from '../../core/state/global-notifications/asyncActions';
import { GlobalNotificationTypes } from '../../core/enums/GlobalNotificationTypes';
import { ProjectSubcontractor } from '../../core/state/subcontractors/types';
import CheckBox from '../../components/form/checkbox/CheckBox';

import './ProjectExternalRoles.scss';

const getBadgeColor = (status: ExternalUserStatus) => {
  switch (status) {
    case ExternalUserStatus.Active:
      return Color.Green;
    case ExternalUserStatus.Invited:
      return Color.Grey;
    case ExternalUserStatus.Locked:
      return Color.Red;
    default:
      return Color.Green;
  }
};

const ProjectExternalRoles: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const users = useSelector((state: RootState) =>
    state.subcontractor.projectSubcontractors?.filter((item) => item.statusEnum !== ExternalUserStatus.Unblocked)
  );
  const [isManageUserModalOpen, setIsManageUserModalOpen] = useState<boolean>(false);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState<boolean>(false);
  const [isSubcontractorModalOpen, setIsSubcontractorModalOpen] = useState<boolean>(false);
  const { projectId } = useParams<{ projectId: string }>();
  const isUserReader = useSelector(isReadAllRole);
  const [isEditExternal, setIsEditExternal] = useState<boolean>(false);
  const [selectedSubcontractor, setSelectedSubcontractor] = useState<number | undefined>(undefined);
  const [IsSendEmail, setIsSendEmail] = useState<boolean>(false);
  const isInternal = useInternalUser();
  const [showHidden, setShowHidden] = useState<boolean>(false);
  const [userToToggle, setUserToToggle] = useState<ProjectSubcontractor | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  React.useEffect(() => {
    dispatch(
      getExternalUsersByProjectId(Number(projectId), [
        ExternalUserStatus.Active,
        ExternalUserStatus.Locked,
        ExternalUserStatus.Invited,
        ExternalUserStatus.Unblocked
      ])
    );
  }, []);

  const getBadgeTitle = (status: ExternalUserStatus) => {
    switch (status) {
      case ExternalUserStatus.Active:
        return t('common.active');
      case ExternalUserStatus.Invited:
        return t('common.invited');
      case ExternalUserStatus.Locked:
        return t('common.locked');
      default:
        return t('common.active');
    }
  };

  const handleSendLoginData = () => {
    if (selectedSubcontractor) {
      dispatch(sendLoginData(Number(projectId), selectedSubcontractor));
    }
    dispatch(
      spawnGlobalNotification({
        type: GlobalNotificationTypes.Success,
        message: t('externalUsers.subcontractorRoles.confirm.succes'),
        title: t('common.notification.success')
      })
    );
    setIsSendEmail(false);
  };

  const handleSubcontractorVisibilityChange = () => {
    if (!userToToggle) return;

    dispatch(changeSubcontractorVisibility(Number(projectId), userToToggle.id, !userToToggle.isHidden));
    setIsConfirmModalOpen(false);
    dispatch(
      spawnGlobalNotification({
        type: GlobalNotificationTypes.Success,
        message: t(`externalUsers.subcontractorRoles.notification.${userToToggle.isHidden ? 'shown' : 'hidden'}`)
      })
    );
  };

  return (
    <TableWrapper>
      <div className="table__header">
        <span>{t('externalUsers.subcontractorRoles.title')}</span>
        {!isUserReader && (
          <div className="d-flex">
            <CheckBox
              id="hidden-subcontractors"
              name="hidden-subcontractors"
              checked={showHidden}
              onChange={() => setShowHidden(!showHidden)}
              label={t('externalUsers.subcontractorRoles.filter')}
              className="project-external-roles__filter"
            />
            <Button
              onClick={() => setIsManageUserModalOpen(true)}
              className="project-external-roles__btn-manage-roles"
              buttonTypes={['blue-outline', 'small']}
            >
              <span>{t('common.manageStatus')}</span>
            </Button>
            <Button onClick={() => setIsAddUserModalOpen(true)} className="project-external-roles__btn-add" buttonTypes={['blue', 'small']}>
              <Icon className="project-external-roles__icon-plus" icon="plus" size="extra-small" svg />
              {t('common.addNew')}
            </Button>
          </div>
        )}
      </div>
      <div className="project-external-roles">
        <Table className="project-external-roles__table">
          <THead>
            <TR>
              <TH>{t('common.companyName')}</TH>
              <TH>
                {t('common.email')} <span className="project-external-roles__additional-info">({t('common.forLogin')})</span>
              </TH>
              <TH>
                {t('common.contactPerson')}
                <br />
                <span className="project-external-roles__additional-info"> ({t('common.forNotifications')})</span>
              </TH>
              <TH>
                {t('common.email')} <span className="project-external-roles__additional-info">({t('common.forNotifications')})</span>
              </TH>
              <TH>{t('externalUsers.subcontractorRoles.form.orderNumber')}</TH>
              <TH>{t('externalUsers.subcontractorRoles.form.source')}</TH>
              <TH>{t('common.createdOn')}</TH>
              <TH>{t('common.addedBy')}</TH>
              <TH>{t('common.status')}</TH>
              <TH />
            </TR>
          </THead>
          <TBody>
            {users
              ?.filter((user) => showHidden || !user.isHidden)
              .map((user) => (
                <TR className={`project-external-roles__user ${user.isHidden ? 'project-external-roles__user--is-hidden' : ''}`} key={user.id}>
                  <TD className="d-flex align-items-center">
                    <div className={`project-external-roles__user-img ${user.isHidden ? 'project-external-roles__user-img--is-hidden' : ''}`}>
                      <Icon
                        className={`project-external-roles__user-icon ${user.isHidden ? 'project-external-roles__user-icon--is-hidden' : ''}`}
                        icon="company"
                        svg
                        size="small"
                      />
                    </div>
                    <span className="font-weight-bold">{user.name}</span>
                  </TD>
                  <TD className="text-left">{user.email}</TD>
                  <TD className="text-left">{user.notificationsContactName}</TD>
                  <TD className="text-left">{user.notificationsEmail}</TD>
                  <TD className="text-left">{user.orderNumber}</TD>
                  <TD className="text-left">
                    {user.createdBy === 'imported'
                      ? t('externalUsers.subcontractorRoles.form.imported')
                      : t('externalUsers.subcontractorRoles.form.manual')}
                  </TD>
                  <TD className="text-left">{format(new Date(user.createdOn), DATE_FORMAT_EU_DOT)}</TD>
                  <TD className="text-left">{user.invitedBy ?? '--'}</TD>
                  <TD className="text-left">
                    <div>
                      <Badge label={getBadgeTitle(user.statusEnum)} color={getBadgeColor(user.statusEnum)} />
                    </div>
                  </TD>
                  <ActionDropdown>
                    <Dropdown.Item
                      onClick={() => {
                        setSelectedSubcontractor(user.id);
                        setIsEditExternal(true);
                      }}
                    >
                      {t('common.edit')}
                    </Dropdown.Item>
                    {user.statusEnum === ExternalUserStatus.Locked && (
                      <Dropdown.Item
                        onClick={() => {
                          setUserToToggle(user);
                          setIsConfirmModalOpen(true);
                        }}
                      >
                        {t(`externalUsers.subcontractorRoles.${user.isHidden ? 'show' : 'hide'}.subcontractor`)}
                      </Dropdown.Item>
                    )}

                    {(user.statusEnum === ExternalUserStatus.Active || user.statusEnum === ExternalUserStatus.Invited) && isInternal && (
                      <Dropdown.Item
                        onClick={() => {
                          setIsSendEmail(true);
                          setSelectedSubcontractor(user.id);
                        }}
                      >
                        {t('externalUsers.subcontractorRoles.sendLogin.dropdown')}
                      </Dropdown.Item>
                    )}
                  </ActionDropdown>
                </TR>
              ))}
          </TBody>
        </Table>
        {!users && (
          <NoResultsPage
            title={t('common.noUsers')}
            description={t('externalUsers.noResults.description')}
            icon="company"
            iconClass="project-external-roles__no-results-icon"
          />
        )}
      </div>
      {isAddUserModalOpen && (
        <AddExternalUserModal
          handleSubcontractorModal={async () => {
            await setIsAddUserModalOpen(false);
            await setIsSubcontractorModalOpen(true);
          }}
          isOpen={isAddUserModalOpen}
          closeModal={() => setIsAddUserModalOpen(false)}
        />
      )}
      {(isSubcontractorModalOpen || isEditExternal) && (
        <ManageExternalUserModal
          isEditExternal={isEditExternal}
          isOpen={isSubcontractorModalOpen || isEditExternal}
          closeModal={() => {
            setIsSubcontractorModalOpen(false);
            setIsEditExternal(false);
          }}
          selectedSubcontractor={selectedSubcontractor}
        />
      )}
      {isManageUserModalOpen && <ManageSubcontractorRoleModal isOpen={isManageUserModalOpen} closeModal={() => setIsManageUserModalOpen(false)} />}
      {IsSendEmail && selectedSubcontractor && (
        <ConfirmModal
          titleText={t('externalUsers.subcontractorRoles.confirm.title')}
          contentText={t('externalUsers.subcontractorRoles.confirm.bodly')}
          isOpen={IsSendEmail}
          onCancel={() => setIsSendEmail(false)}
          onConfirm={handleSendLoginData}
        />
      )}
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        titleText={t(`externalUsers.subcontractorRoles.${userToToggle?.isHidden ? 'show' : 'hide'}.subcontractor`)}
        contentText={t(`externalUsers.subcontractorRoles.confirm.${userToToggle?.isHidden ? 'show' : 'hide'}.text`)}
        onCancel={() => setIsConfirmModalOpen(false)}
        onConfirm={() => {
          if (userToToggle) {
            handleSubcontractorVisibilityChange();
          }
        }}
      />
    </TableWrapper>
  );
};

export default ProjectExternalRoles;
